import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FiFileText, FiEdit, FiTrash2, FiEye, FiPlus, FiSearch, FiFilter, FiSave, FiX } from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { API_URL, API_URL_Image } from '../api/api';
const token = localStorage.getItem('token');

const TermsConditionLayer = () => {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingId, setViewingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    language: 'en',
    version: '',
    status: 'draft',
    is_default: false
  });

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Hindi' },
  ];

  const statusOptions = [
    { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-800' },
    { value: 'active', label: 'Active', color: 'bg-green-100 text-green-800' },
    { value: 'inactive', label: 'Inactive', color: 'bg-red-100 text-red-800' },
    { value: 'archived', label: 'Archived', color: 'bg-yellow-100 text-yellow-800' }
  ];

  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/terms-conditions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Handle paginated response (Laravel returns data in data.data for paginated responses)
      if (response.data && response.data.data) {
        // If using Laravel pagination
        setTerms(response.data.data);
      } else if (Array.isArray(response.data)) {
        // If API returns direct array
        setTerms(response.data);
      } else {
        // Fallback
        setTerms([]);
      }

    } catch (error) {
      console.error('Error fetching terms:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load terms and conditions.'
      });
      setTerms([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleContentChange = (value) => {
    setFormData(prev => ({ ...prev, content: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Title is required.'
      });
      return;
    }

    if (!formData.content.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Content is required.'
      });
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${API_URL}/terms-conditions/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire({
          icon: 'success',
          title: 'Updated!',
          text: 'Terms & Conditions updated successfully.',
          timer: 2000
        });
      } else {
        await axios.post(`${API_URL}/terms-conditions`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        Swal.fire({
          icon: 'success',
          title: 'Created!',
          text: 'Terms & Conditions created successfully.',
          timer: 2000
        });
      }

      resetForm();
      fetchTerms();
    } catch (error) {
      console.error('Error saving terms:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to save terms and conditions.'
      });
    }
  };

  const handleEdit = (term) => {
    setFormData({
      title: term.title,
      content: term.content,
      language: term.language,
      version: term.version,
      status: term.status,
      is_default: term.is_default
    });
    setEditingId(term.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleView = (term) => {
    setViewingId(term.id);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_URL}/terms-conditions/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Terms & Conditions deleted successfully.',
          timer: 2000
        });

        fetchTerms();
      } catch (error) {
        console.error('Error deleting terms:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete terms and conditions.'
        });
      }
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await axios.put(`${API_URL}/terms-conditions/${id}/set-default`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      Swal.fire({
        icon: 'success',
        title: 'Updated!',
        text: 'Default terms & conditions updated.',
        timer: 2000
      });

      fetchTerms();
    } catch (error) {
      console.error('Error setting default:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to set as default.'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      language: 'en',
      version: '',
      status: 'draft',
      is_default: false
    });
    setEditingId(null);
    setShowForm(false);
  };
  const filteredTerms = (terms && Array.isArray(terms)) ? terms.filter(term => {
    if (!term) return false;

    const matchesSearch = term.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      term.content?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || term.status === filterStatus;
    return matchesSearch && matchesStatus;
  }) : [];
  const getLanguageName = (code) => {
    const lang = languages.find(l => l.code === code);
    return lang ? lang.name : code;
  };

  const getStatusInfo = (status) => {
    return statusOptions.find(s => s.value === status) || statusOptions[0];
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link', 'image', 'video'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link', 'image', 'video',
    'color', 'background'
  ];

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-gradient-primary text-white py-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">
                    <FiFileText className="me-2" />
                    Terms & Conditions Management
                  </h5>
                  <p className="mb-0 opacity-75 small">
                    Create, edit, and manage website terms and conditions
                  </p>
                </div>
                <button
                  onClick={() => setShowForm(!showForm)}
                  className="btn btn-light btn-sm"
                >
                  {showForm ? <FiX className="me-1" /> : <FiPlus className="me-1" />}
                  {showForm ? 'Cancel' : 'Add New'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-header bg-light py-3">
                <h6 className="mb-0">
                  <FiEdit className="me-2" />
                  {editingId ? 'Edit Terms & Conditions' : 'Create New Terms & Conditions'}
                </h6>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-md-8">
                      <label className="form-label fw-semibold">Title *</label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Enter terms & conditions title"
                        required
                      />
                    </div>

                    <div className="col-md-2">
                      <label className="form-label fw-semibold">Language</label>
                      <select
                        name="language"
                        value={formData.language}
                        onChange={handleInputChange}
                        className="form-select"
                      >
                        {languages.map(lang => (
                          <option key={lang.code} value={lang.code}>
                            {lang.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-2">
                      <label className="form-label fw-semibold">Version</label>
                      <input
                        type="text"
                        name="version"
                        value={formData.version}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="e.g., 1.0.0"
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold">Content *</label>
                      <ReactQuill
                        theme="snow"
                        value={formData.content}
                        onChange={handleContentChange}
                        modules={quillModules}
                        formats={quillFormats}
                        style={{ marginBottom: '50px' }}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Status</label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className="form-select"
                      >
                        {statusOptions.map(status => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6 d-flex align-items-end">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          name="is_default"
                          checked={formData.is_default}
                          onChange={handleInputChange}
                          className="form-check-input"
                          id="isDefault"
                        />
                        <label className="form-check-label" htmlFor="isDefault">
                          Set as default version
                        </label>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="d-flex justify-content-end gap-2">
                        <button
                          type="button"
                          onClick={resetForm}
                          className="btn btn-outline-secondary px-4"
                        >
                          <FiX className="me-1" /> Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary px-4"
                        >
                          <FiSave className="me-1" />
                          {editingId ? 'Update' : 'Create'}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingId && terms && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-light">
                <h5 className="modal-title">
                  <FiEye className="me-2" />
                  View Terms & Conditions
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setViewingId(null)}
                ></button>
              </div>
              <div className="modal-body">
                {terms.find(t => t && t.id === viewingId) && (
                  <>
                    <h4 className="mb-3">{terms.find(t => t.id === viewingId).title}</h4>
                    <div className="d-flex gap-3 mb-4">
                      <span className="badge bg-primary">
                        {getLanguageName(terms.find(t => t.id === viewingId).language)}
                      </span>
                      <span className={`badge ${getStatusInfo(terms.find(t => t.id === viewingId).status).color}`}>
                        {getStatusInfo(terms.find(t => t.id === viewingId).status).label}
                      </span>
                      {terms.find(t => t.id === viewingId).version && (
                        <span className="badge bg-info">
                          Version: {terms.find(t => t.id === viewingId).version}
                        </span>
                      )}
                      {terms.find(t => t.id === viewingId).is_default && (
                        <span className="badge bg-success">
                          Default
                        </span>
                      )}
                    </div>
                    <div
                      className="ql-editor border rounded p-3"
                      dangerouslySetInnerHTML={{
                        __html: terms.find(t => t.id === viewingId).content
                      }}
                    />
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setViewingId(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-8">
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <FiSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search terms & conditions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <FiFilter />
                    </span>
                    <select
                      className="form-select border-start-0"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      {statusOptions.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Terms List */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading terms & conditions...</p>
                </div>
              ) : filteredTerms.length === 0 ? (
                <div className="text-center py-5">
                  <FiFileText size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">No terms & conditions found</h5>
                  <p className="text-muted">
                    {searchTerm || filterStatus !== 'all'
                      ? 'Try changing your search or filter'
                      : 'Click "Add New" to create your first terms & conditions'}
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Language</th>
                        <th>Version</th>
                        <th>Status</th>
                        <th>Default</th>
                        <th>Last Updated</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTerms.map(term => (
                        <tr key={term.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <FiFileText className="text-primary me-2" />
                              <div>
                                <strong>{term.title}</strong>
                                <div className="text-muted small">
                                  {term.content.substring(0, 100)}...
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="badge bg-light text-dark">
                              {getLanguageName(term.language)}
                            </span>
                          </td>
                          <td>
                            {term.version ? (
                              <span className="badge bg-info">{term.version}</span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${getStatusInfo(term.status).color}`}>
                              {getStatusInfo(term.status).label}
                            </span>
                          </td>
                          <td>
                            {term.is_default ? (
                              <span className="badge bg-success">Default</span>
                            ) : (
                              <button
                                onClick={() => handleSetDefault(term.id)}
                                className="btn btn-sm btn-outline-success"
                                title="Set as default"
                              >
                                Set Default
                              </button>
                            )}
                          </td>
                          <td>
                            <small className="text-muted">
                              {new Date(term.updated_at).toLocaleDateString()}
                            </small>
                          </td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button
                                onClick={() => handleView(term)}
                                className="btn btn-outline-info"
                                title="View"
                              >
                                <FiEye />
                              </button>
                              <button
                                onClick={() => handleEdit(term)}
                                className="btn btn-outline-primary"
                                title="Edit"
                              >
                                <FiEdit />
                              </button>
                              <button
                                onClick={() => handleDelete(term.id)}
                                className="btn btn-outline-danger"
                                title="Delete"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {filteredTerms.length > 0 && (
              <div className="card-footer bg-light">
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    Showing {filteredTerms.length} of {terms.length} terms & conditions
                  </small>
                  <small className="text-muted">
                    {terms.filter(t => t.is_default).length} default version(s)
                  </small>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      {/* Quick Stats */}
      <div className="row mt-4">
        <div className="col-md-3 col-sm-6">
          <div className="card border-0 shadow-sm bg-gradient-primary text-white">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-0 opacity-75">Total</h6>
                  <h3 className="mb-0">{terms && terms.length ? terms.length : 0}</h3>
                </div>
                <FiFileText size={32} />
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6">
          <div className="card border-0 shadow-sm bg-gradient-success text-white">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-0 opacity-75">Active</h6>
                  <h3 className="mb-0">
                    {terms && terms.filter ? terms.filter(t => t.status === 'active').length : 0}
                  </h3>
                </div>
                <FiFileText size={32} />
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6">
          <div className="card border-0 shadow-sm bg-gradient-info text-white">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-0 opacity-75">Languages</h6>
                  <h3 className="mb-0">
                    {terms && terms.length ? [...new Set(terms.map(t => t.language))].length : 0}
                  </h3>
                </div>
                <FiFileText size={32} />
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3 col-sm-6">
          <div className="card border-0 shadow-sm bg-gradient-warning text-white">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-0 opacity-75">Default</h6>
                  <h3 className="mb-0">
                    {terms && terms.filter ? terms.filter(t => t.is_default).length : 0}
                  </h3>
                </div>
                <FiFileText size={32} />
              </div>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};

export default TermsConditionLayer;